import { useEffect, useMemo, useState } from "react";
import { PDFViewer, pdf } from "@react-pdf/renderer";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { getLocalizedDate } from "../../../functions/localization";

import { registerPoppins } from "../../../fonts/registerFonts";
import PDFHeaderControls from "./PDFHeaderControls";
import PDFPagination from "./PDFPagination";
import PDFReportDocument from "./PDFReportDocument";
import {
  computeVariableColumnWidths,
  PDF_PAGE_CONTENT_WIDTH,
} from "./pdfColumnWidths";
import { PDF_PREVIEW_ROWS, getPdfPreviewPageCount } from "../reportExport";

registerPoppins();

const MyView = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 30px 20px;
  height: 100%;
  max-height: 1024px;
  min-height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 75px;
  min-height: 75px;
  border-bottom: 1px solid #a7bbcf;
`;

const PDFReportViewer = ({
  user,
  title,
  headings,
  groupedField,
  data,
  columnSums,
  columnAverages,
  selectionRange,
  onBack,
  showAVGRow,
  showSUMRow,
  headingsSpacing,
  dataSpacing,
  defaultFontSize,
}) => {
  const { t } = useTranslation("pdfReportViewer");

  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [columnWidthTypeRadioValue, setColumnWidthTypeRadioValue] = useState(1);
  const [fontSizeRadioValue, setFontSizeRadioValue] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 2 : 1,
  );

  const [headingsAndDataFontSize, setHeadingsAndDataFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 8 : 10,
  );
  const [sumAndAvgFontSize, setSumAndAvgFontSize] = useState(() =>
    defaultFontSize?.toLowerCase() === "small" ? 10 : 12,
  );

  useEffect(() => {
    if (fontSizeRadioValue === 1) {
      setHeadingsAndDataFontSize(10);
      setSumAndAvgFontSize(12);
    } else {
      setHeadingsAndDataFontSize(8);
      setSumAndAvgFontSize(10);
    }
  }, [fontSizeRadioValue]);

  const handleIsLoading = (boolVal) => {
    setIsLoading(boolVal);
  };

  const allowCustomWidths =
    headingsSpacing?.length === headings.length &&
    dataSpacing?.length === headings.length;

  useEffect(() => {
    if (allowCustomWidths) setColumnWidthTypeRadioValue(3);
  }, []);

  // "Equal Widths": the page's usable width shared evenly. (This was 760,
  // 8pt more than the page has, so react-pdf shrank every column by ~1%.)
  const columnWidth = PDF_PAGE_CONTENT_WIDTH / headings.length;

  /* JFCRX-6733 (QA 2026-10-30). The width arithmetic that stood here scaled
   * every column by one factor to fit the page, which on every real
   * Production export pushed the NDC column narrower than its digits and ran
   * them into the next column. It now lives in pdfColumnWidths.js, where it
   * pins each column at its longest unbreakable token and follows the font
   * size -- hence headingsAndDataFontSize in the dependency list.
   * (Grouped rows arrive already flattened, so the scan is the same either way.) */
  const variableColumnWidthsArray = useMemo(
    () =>
      computeVariableColumnWidths({
        headings,
        data,
        fontSize: headingsAndDataFontSize,
      }),
    [headings, data, groupedField, headingsAndDataFontSize],
  );

  const previewData = useMemo(
    () =>
      data.slice(
        currentPage * PDF_PREVIEW_ROWS,
        (currentPage + 1) * PDF_PREVIEW_ROWS,
      ),
    [data, currentPage],
  );

  /* JFCRX-7643. The preview is one ten-row document per pager page, so
   * react-pdf's own counter inside it always reads "1 / 1". The preview
   * footer states the preview position instead, counted the same way the
   * pager counts. The download passes no label and keeps real page numbers. */
  const previewFooterLabel = t("previewPageLabel", {
    page: currentPage + 1,
    total: getPdfPreviewPageCount(data.length),
  });

  const startDate = selectionRange?.startDate
    ? getLocalizedDate(
        selectionRange.startDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;
  const endDate = selectionRange?.endDate
    ? getLocalizedDate(
        selectionRange.endDate,
        user?.defaultSite?.shippingAddress?.country,
      )
    : null;

  const createDocument = (dataSet, isDownload = false) => (
    <PDFReportDocument
      title={title}
      headings={headings}
      data={dataSet}
      columnWidth={columnWidth}
      fontSize={fontSizeRadioValue === 2 ? 8 : 10}
      startDate={startDate}
      endDate={endDate}
      variableColumnWidthsArray={variableColumnWidthsArray}
      columnWidthTypeRadioValue={columnWidthTypeRadioValue}
      headingsAndDataFontSize={headingsAndDataFontSize}
      sumAndAvgFontSize={sumAndAvgFontSize}
      columnSums={columnSums}
      columnAverages={columnAverages}
      showSUMRow={showSUMRow}
      showAVGRow={showAVGRow}
      headingsSpacing={headingsSpacing}
      dataSpacing={dataSpacing}
      isDownload={isDownload}
      footerLabel={isDownload ? undefined : previewFooterLabel}
    />
  );

  const handleDownload = () => {
    setIsLoading(true);

    // Defer to next event loop tick to allow spinner to render
    setTimeout(async () => {
      try {
        const doc = createDocument(data, true);
        const blobGenerator = pdf([]);
        blobGenerator.updateContainer(doc);
        const blob = await blobGenerator.toBlob();
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `${title.replace(/\s+/g, "_")}_Report.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed", err);
      } finally {
        setIsLoading(false);
      }
    }, 0); // ← allow render before blocking
  };

  return (
    <MyView>
      <Header>
        <PDFHeaderControls
          t={t}
          title={title}
          onBack={onBack}
          allowCustomWidths={allowCustomWidths}
          columnWidthTypeRadioValue={columnWidthTypeRadioValue}
          setColumnWidthTypeRadioValue={setColumnWidthTypeRadioValue}
          fontSizeRadioValue={fontSizeRadioValue}
          setFontSizeRadioValue={setFontSizeRadioValue}
          onDownload={handleDownload}
          isLoading={isLoading}
          onIsLoading={handleIsLoading}
        />
      </Header>

      {/* JFCRX-7643. The plugin toolbar's page count can only describe the
          ten-row blob on screen, so it is hidden in the preview. */}
      <PDFViewer width="100%" height="86%" showToolbar={false}>
        {createDocument(previewData)}
      </PDFViewer>

      <PDFPagination
        totalItems={data.length}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        pageSize={PDF_PREVIEW_ROWS}
      />
    </MyView>
  );
};

export default PDFReportViewer;
